import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
  createSelector,
  ThunkDispatch,
  AnyAction,
} from '@reduxjs/toolkit';
import {
  SbSocketErrorCode,
  SbSocketStatus,
  SbSocketStatusPackage,
} from 'sb-lib/sb-socket-abstract';
import {
  VisitorUpdates,
  VisitorInteractionUpdate,
  PartialVisitor,
  SubscribeUnsubscribeData,
  CallEndedDetailsPayload,
} from 'sb-lib/types';
import {
  Client,
  PeerStatus,
  WidgetTriggerData,
  Trigger,
  WidgetState,
  SelfUser,
  UnionToIntersection,
  InteractionStatus,
  VisitorAssociationStatus,
} from 'types';
import type { AppState } from './reducers';
import {
  selectActivePublicUser,
  selectActiveUser,
  selectActiveVisitor,
} from './shared-selectors';
import {
  connectSocket,
  getIncomingAudioStream,
  getIncomingCameraStream,
  getIncomingVideoStream,
  getIncomingDisplayStream,
  getOutgoingCameraStream,
  getOutgoingDisplayStream,
  introduceSocket,
  disconnectSocket,
  subscribeToChannel as managerSubscribeToChannel,
  unsubscribeFromChannel as managerUnsubscribeFromChannel,
  relayRrwebReset,
  resetSpyOptin,
} from 'shared/realtime';
import { kits } from 'shared/realtime/kits';
import { makeLog } from 'shared/utils/log';
import { detectAssociatedVisitorIdChange, makeDefaultVisitor } from './data-util';
import { checkAccount, refreshActiveVisitor } from './shared-actions';
import { dissociateVisitor } from 'shared/realtime/association-kit';
import { receivedUnreadInboxMessageCount } from './conversation-slice';
import { AppDispatch } from './store';
import { getCurrentVisitors } from 'shared/api';

const log = makeLog('realtime-slice');

const kitReducers = kits.reduce(
  (reducers, kit) => ({ ...reducers, ...kit.reducers }),
  {} as UnionToIntersection<typeof kits[number]['reducers']>,
);

type PA<T> = PayloadAction<T>;
type Visitor = Client;
interface ServerStatusPackage extends SbSocketStatusPackage {
  sid: string | null;
}
/**
 * Data
 */

const initialWidgetState: WidgetState = {
  speakerStatus: 'off',
  micStatus: 'off',
  camStatus: 'off',
};

const getInitialState = () => ({
  lastServerDisconnectTime: 0,
  serverStatus: 'disconnected' as SbSocketStatus,
  serverCommitHash: '',
  serverProtocolVersion: null as null | number,
  serverTimeDelta: null as null | number,
  sid: null as null | string,

  isConnectServerAction: false,
  isConnectingToServer: false,
  isConnectedToServer: false,
  serverErrorCode: null as SbSocketStatusPackage['disconnectCode'],
  hasCheckedBadAccount: false,

  visitorMapSyncCount: 0,
  visitorMap: {} as Record<string, Visitor>,
  visitorsCount: { total: 0, streams: {} as Record<number, number> },

  peerStatus: 'disconnected' as PeerStatus,
  peerProtocolVersion: null as null | number,
  peerConnectionError: '',
  isConnectedToPeer: false,
  isConnectingToPeer: false,
  isReconnectingToPeer: false,

  widgetState: initialWidgetState,

  intendedVisitorId: null as null | string,
  optimisticInteractions: {} as VisitorInteractionUpdate,

  associationStatus: 'off' as VisitorAssociationStatus,
  associationStatusMsg: null as null | string,
  dissociatingFromSbid: null as null | string,

  takeoverStatus: 'off' as InteractionStatus,

  callStatus: 'off' as InteractionStatus,
  callStatusMsg: null as null | string,
  isCallWantedPending: false,

  isDisplayshareWanted: false,
  displayshareStatus: 'off' as InteractionStatus,

  outgoingCameraStreamId: null as null | string,
  outgoingDisplayStreamId: null as null | string,
  incomingCameraStreamId: null as null | string,
  incomingDisplayStreamId: null as null | string,
  isReceivingAudio: false,
  isReceivingVideo: false,

  callEndedDetails: null as CallEndedDetailsPayload | null,
});
export const initialState = getInitialState();

export type RealtimeState = typeof initialState;
export type TriggerHandler = (
  trigger: Trigger,
  state: AppState,
  dispatch: ThunkDispatch<AppState, AnyAction, any>,
) => void;

/**
 * Thunks
 */
export const connectServer = createAsyncThunk<
  void,
  { passive: boolean; kickOther?: boolean; reason?: string },
  { state: AppState }
>('realtime/connectServer', async ({ passive, kickOther, reason }, { getState }) => {
  const user = selectActiveUser(getState());
  const publicUser = selectActivePublicUser(getState());
  const socketUser = user ? user : publicUser;
  if (!socketUser) {
    log.warn('Tried to connect to socket without authenticated user present');
    return;
  }
  const { isConnectingToServer, isConnectedToServer } = getState().realtime;
  // Only ignore if not both accounts are valid (switching account scenario)
  if ((isConnectedToServer || isConnectingToServer) && !(user && publicUser)) {
    return; // just ignore
  }
  await connectSocket(socketUser.org.id, !!kickOther, passive, reason || 'thunk');
});

export const sendResetSpyOptin = createAsyncThunk<void, void, { state: AppState }>(
  'realtime/sendResetSpyOptin',
  async (_, { getState }) => {
    const visitor = selectRealtimeAssociatedVisitor(getState());
    if (visitor) {
      await resetSpyOptin(visitor.sbid);
    }
  },
);

export const socketStatusChanged = createAsyncThunk<
  void,
  SbSocketStatusPackage,
  { state: AppState }
>(
  'realtime/socketStatusChanged',
  async ({ status, disconnectCode }, { dispatch, getState }) => {
    const wasConnected = getState().realtime.serverStatus === 'connected';
    const currentSid = getState().realtime.sid;
    if (!wasConnected && status === 'connected') {
      try {
        // send intro and handle, then update serverStatus
        const {
          sid,
          buildInformation,
          visitorsCount,
          unreadInboxMessageCount,
        } = await introduceSocket();
        const {
          data: { data: visitorsSync },
        } = await getCurrentVisitors();
        log.debug(`Connected to ServiceBell (sid=${sid})`);
        dispatch(serverStatusChanged({ sid, status, disconnectCode }));
        dispatch(visitorsSynced(visitorsSync));
        dispatch(
          setVisitorsCount({
            total: visitorsSync.length,
            streams: visitorsCount.streams,
          }),
        );
        dispatch(receivedServerBuildInfo(buildInformation));
        dispatch(receivedUnreadInboxMessageCount(unreadInboxMessageCount));
      } catch (error) {
        log.debug(`Introduction failed [${error}]`);
        if (error === SbSocketErrorCode.ACTION_RESPONSE_TIMEOUT) {
          // We will get this if the introduction failed outside of our backend (apigw)
          disconnectSocket('introduction failed (timeout)');
          // make sure redux thinks we are disconnected
          dispatch(
            serverStatusChanged({
              sid: null,
              status: 'disconnected',
              disconnectCode: null,
            }),
          );
          // attempt to connect again
          dispatch(
            connectServer({
              passive: true,
              kickOther: false,
              reason: 'introduction failure retry',
            }),
          );
        }
      }
    } else {
      dispatch(serverStatusChanged({ sid: currentSid, status, disconnectCode }));
    }
  },
);

export const receivedTrigger = createAsyncThunk<
  WidgetTriggerData,
  WidgetTriggerData,
  { state: AppState }
>('realtime/receivedTrigger', async (data, { dispatch, getState }) => {
  const { trigger } = data;
  log.debug(`Trigger from widget "${trigger}"`);

  const state = getState();
  kits.forEach(kit => kit.triggerThunkHelper(trigger, state, dispatch as AppDispatch));

  return data;
});

export const visitorsSynced = createAsyncThunk<
  { list: PartialVisitor[]; authedUser: null | SelfUser },
  PartialVisitor[],
  { state: AppState }
>('realtime/visitorsSynced', async (list, { dispatch, getState }) => {
  const state = getState();
  const authedUser = selectActiveUser(state);
  // Dissociate from any visitor we might still be associated to
  // from another tab, or after changing the url by hand
  const { intendedVisitorId } = state.realtime;
  if (authedUser && !intendedVisitorId) {
    const associatedVisitor = list.find(v => v.helpedBy === authedUser.id);
    if (associatedVisitor) {
      log.debug(
        `Dissociating because visitor is helped by agent, but local intendedVisitorId unset`,
      );
      void dispatch(dissociateVisitor({ sbid: associatedVisitor.sbid }));
    }
  }

  return { list, authedUser };
});

export type AssociatedVisitorIdSwap = null | {
  oldSbid: string;
  newSbid: string;
  visitorUpdate: PartialVisitor;
};
export const visitorsUpdated = createAsyncThunk<
  {
    updates: VisitorUpdates;
    authedUser: null | SelfUser;
    associatedVisitorIdSwap: AssociatedVisitorIdSwap;
  },
  VisitorUpdates,
  { state: AppState }
>('realtime/visitorsUpdated', async (updates, { dispatch, getState }) => {
  const state = getState();
  const authedUser = selectActiveUser(getState());
  const idSwapAssocVisitor = detectAssociatedVisitorIdChange(state, updates);
  let associatedVisitorIdSwap: AssociatedVisitorIdSwap = null;
  if (idSwapAssocVisitor) {
    const oldSbid = state.realtime.intendedVisitorId;
    const newSbid = idSwapAssocVisitor.sbid;
    if (oldSbid) {
      log.debug(`Associated sbid appears to have changed from ${oldSbid} to ${newSbid}`);
      associatedVisitorIdSwap = { oldSbid, newSbid, visitorUpdate: idSwapAssocVisitor };
    }
  }
  // If the active visitor's email changed, then refresh it because
  // it's likely been associated with a contact.
  const activeVisitor = state.dashboard.activeVisitor;
  if (activeVisitor) {
    Object.entries(updates).forEach(([_, upd]) => {
      if (upd.type === 'UPDATE' && upd.data.sbid == activeVisitor.sbid) {
        if (upd.data.email && activeVisitor.email !== upd.data.email) {
          dispatch(refreshActiveVisitor());
        }
      }
    });
  }
  return { updates, authedUser, associatedVisitorIdSwap };
});

export const visitorInteractionUpdated = createAsyncThunk<
  void,
  Visitor,
  { state: AppState }
>('realtime/visitorInteractionUpdated', async (visitor, { dispatch }) => {
  const updates = { [visitor.sbid]: { type: 'UPDATE', data: visitor } } as const;
  dispatch(visitorsUpdated(updates));
});

export const subscribeToChannel = createAsyncThunk<
  void,
  SubscribeUnsubscribeData,
  { state: AppState }
>('realtime/subscribeToChannel', async ({ channelType, channelIdentifier }) => {
  try {
    await managerSubscribeToChannel({ channelType, channelIdentifier });
  } catch (e) {
    throw new Error(
      `Server denied subscribe to channel ${channelType}.${channelIdentifier}`,
    );
  }
});

export const unsubscribeFromChannel = createAsyncThunk<
  void,
  SubscribeUnsubscribeData,
  { state: AppState }
>('realtime/unsubscribeFromChannel', async ({ channelType, channelIdentifier }) => {
  try {
    await managerUnsubscribeFromChannel({ channelType, channelIdentifier });
  } catch (e) {
    throw new Error(
      `Server denied unsubscribe from channel ${channelType}.${channelIdentifier}`,
    );
  }
});

export const sendResetRrweb = createAsyncThunk<void, void, { state: AppState }>(
  'realtime/sendResetRrweb',
  async (_, { getState }) => {
    const visitor = selectRealtimeAssociatedVisitor(getState());
    if (visitor && visitor.sid) {
      relayRrwebReset(visitor.sid);
    } else {
      log.debug('Tried to send reset rrweb to visitor but no visitor sid available');
    }
  },
);

/**
 * Slice
 */
const realtimeSlice = createSlice({
  name: 'realtime',
  initialState,
  reducers: {
    resetRealtimeState() {
      return getInitialState();
    },
    serverStatusChanged(state, action: PA<ServerStatusPackage>) {
      const { sid, status, disconnectCode } = action.payload;
      const lastServerStatus = state.serverStatus;
      state.serverStatus = status;
      state.isConnectedToServer = status === 'connected';
      if (lastServerStatus === 'connected' && status === 'disconnected') {
        state.lastServerDisconnectTime = Date.now();
      }
      state.isConnectingToServer = ['connecting', 'reconnecting'].includes(status);
      state.sid = sid;
      state.serverErrorCode = disconnectCode;

      if (status !== 'disconnected') {
        state.serverErrorCode = null;
        state.hasCheckedBadAccount = false;
      }
    },
    receivedServerBuildInfo(
      state,
      action: PA<{
        commitHash: string;
        protocolVersion: number;
      }>,
    ) {
      const p = action.payload;
      state.serverCommitHash = p.commitHash;
      state.serverProtocolVersion = p.protocolVersion;
    },
    receivedWidgetState(state, action: PA<WidgetState>) {
      state.widgetState = action.payload;
    },
    serverTimeReceived(state, action: PA<number>) {
      state.serverTimeDelta = action.payload - Date.now();
    },

    setVisitorsCount(
      state,
      action: PA<{ total: number; streams: Record<number, number> }>,
    ) {
      state.visitorsCount = action.payload;
    },

    peerConnectionErrorUpdate(state, action: PA<string>) {
      state.peerConnectionError = action.payload;
    },

    recordingWanted(state, action: PA<boolean>) {
      if (state.optimisticInteractions.isRecording !== action.payload) {
        state.optimisticInteractions = {
          ...state.optimisticInteractions,
          isRecording: action.payload,
        };
      }
    },

    callEndedDetailsChanged(state, action: PA<CallEndedDetailsPayload | null>) {
      state.callEndedDetails = action.payload;
    },

    // Don't forget to add these to the realtimeSlice.actions exports
    // Reducer function types must be explicitly defined in the kit for destructuring to work
    ...kitReducers,
  },

  extraReducers: builder => {
    builder.addCase(connectServer.pending, state => {
      state.isConnectServerAction = true;
    });
    builder.addCase(connectServer.rejected, state => {
      state.isConnectServerAction = false;
    });
    builder.addCase(connectServer.fulfilled, state => {
      state.isConnectServerAction = false;
    });

    builder.addCase(visitorsSynced.fulfilled, (state, action) => {
      state.visitorMap = action.payload.list.reduce((visitorMap, visitor) => {
        return {
          [visitor.sbid]: {
            ...makeDefaultVisitor(),
            ...visitor,
          },
          ...visitorMap,
        };
      }, {} as typeof state.visitorMap);
      state.visitorMapSyncCount++;

      // kit reducers
      kits.forEach(k => k.visitorUpdateReducer(state, action.payload.authedUser?.id));
    });

    builder.addCase(visitorsUpdated.fulfilled, (state, action) => {
      // Handle associated visitor id change
      const { associatedVisitorIdSwap } = action.payload;
      if (associatedVisitorIdSwap && state.intendedVisitorId) {
        const { oldSbid, newSbid, visitorUpdate } = associatedVisitorIdSwap;
        const currentVisitor = state.visitorMap[state.intendedVisitorId];
        if (currentVisitor && currentVisitor.sid === visitorUpdate.sid) {
          log.debug(
            `Merging updated associated visitor with sbid change into current state (oldSbid=${oldSbid}, newSbid=${newSbid})`,
          );
          // Replace the wanted visitor id and merge
          state.intendedVisitorId = newSbid;
          state.visitorMap[newSbid] = {
            ...currentVisitor,
            ...action.payload,
          };
        } else {
          log.warn(
            `Could not merge associated visitor update with changed sbid because sids differ (oldSbid=${oldSbid}, newSbid=${newSbid})`,
          );
        }
      }

      Object.entries(action.payload.updates).forEach(([_, upd]) => {
        if (upd.type === 'REMOVE') {
          delete state.visitorMap[upd.data.sbid];
        } else if (upd.type === 'UPDATE') {
          // Only update visitor with empty sid (and ring bell) if they are already in the list
          const isUpdatedVisitorInMap = !!state.visitorMap[upd.data.sbid];
          const udpatedVisitorHasSid = !!upd.data.sid;
          if (!udpatedVisitorHasSid && !isUpdatedVisitorInMap) return;
          const updatedVisitor = {
            ...makeDefaultVisitor(),
            ...upd.data,
          };

          state.visitorMap[upd.data.sbid] = updatedVisitor;
        }
      });
      // so it is detected as a change by selectors
      state.visitorMap = { ...state.visitorMap };

      // kit reducers
      kits.forEach(k => k.visitorUpdateReducer(state, action.payload.authedUser?.id));
    });

    builder.addCase(checkAccount.pending, state => {
      state.hasCheckedBadAccount = true;
    });

    builder.addCase(visitorInteractionUpdated.pending, state => {
      state.optimisticInteractions = {};
    });

    // Add cases for all kit thunks / thunkReducers
    kits.forEach(kit => {
      if ('extraReducers' in kit) {
        kit.extraReducers(builder);
      }
    });
  },
});

/**
 * Selectors
 */
export const selectIntendedSbid = createSelector(
  (state: AppState) => state.realtime.intendedVisitorId,
  sbid => sbid,
);

export const selectServerDisconnected = createSelector(
  (state: AppState) => state.realtime.serverErrorCode,
  serverErrorCode =>
    serverErrorCode &&
    [
      SbSocketErrorCode.STALE,
      SbSocketErrorCode.DISPLACED,
      SbSocketErrorCode.CONNECTION_DATA_NOT_FOUND,
    ].includes(serverErrorCode),
);

export const selectHaveRealtimeVisitorsLoaded = createSelector(
  (state: AppState) => state.realtime.visitorMapSyncCount,
  visMapSyncCount => visMapSyncCount > 0,
);

export const selectRealtimeVisitor = createSelector(
  (state: AppState) => state.realtime.visitorMap,
  (_: AppState, visitorId: string) => visitorId,
  (visitorMap, visitorId) => visitorMap[visitorId],
);

export const selectRealtimeIntendedVisitor = createSelector(
  (state: AppState) => state.realtime.visitorMap,
  (state: AppState) => state.realtime.intendedVisitorId,
  (visMap, actVisId) => (actVisId ? visMap[actVisId] : null),
);

export const selectRealtimeAssociatedVisitor = createSelector(
  selectRealtimeIntendedVisitor,
  (state: AppState) => state.realtime.sid,
  (actVis, sid) =>
    actVis && actVis.helpedBySid && actVis.helpedBySid === sid ? actVis : null,
);

export const selectIsActiveVisitorOnline = createSelector(
  selectRealtimeIntendedVisitor,
  actVis => {
    return !!actVis?.sid;
  },
);

// this looks through all visitors for unexpected association(s)
export const selectFindAssociatedRealtimeVisitor = createSelector(
  (state: AppState) => state.realtime.visitorMap,
  (state: AppState) => state.realtime.sid,
  (visMap, sid) => {
    const associated = Object.entries(visMap).find(([_, v]) => {
      return v.helpedBySid === sid;
    });
    if (associated) {
      return associated[1];
    }
  },
);

export const selectOutgoingCameraStream = createSelector(
  (state: AppState) => state.realtime.outgoingCameraStreamId,
  streamId => {
    if (streamId) {
      return getOutgoingCameraStream();
    }
    return null;
  },
);

export const selectIncomingCameraStream = createSelector(
  (state: AppState) => state.realtime.incomingCameraStreamId,
  (state: AppState) => state.realtime.isConnectedToPeer,
  (streamId, isConnectedToPeer) => {
    if (streamId && isConnectedToPeer) {
      return getIncomingCameraStream();
    }
    return null;
  },
);

export const selectIncomingCameraVideoStream = createSelector(
  (state: AppState) => state.realtime.incomingCameraStreamId,
  (state: AppState) => state.realtime.isConnectedToPeer,
  (state: AppState) => state.realtime.isReceivingVideo,
  (streamId, isConnectedToPeer) => {
    if (streamId && isConnectedToPeer) {
      return getIncomingVideoStream();
    }
    return null;
  },
);

export const selectIncomingCameraAudioStream = createSelector(
  (state: AppState) => state.realtime.incomingCameraStreamId,
  (state: AppState) => state.realtime.isConnectedToPeer,
  (state: AppState) => state.realtime.isReceivingAudio,
  (streamId, isConnectedToPeer) => {
    if (streamId && isConnectedToPeer) {
      return getIncomingAudioStream();
    }
    return null;
  },
);

export const selectIncomingDisplayStream = createSelector(
  (state: AppState) => state.realtime.incomingDisplayStreamId,
  (state: AppState) => state.realtime.isConnectedToPeer,
  (streamId, isConnectedToPeer) => {
    if (streamId && isConnectedToPeer) {
      return getIncomingDisplayStream();
    }
    return null;
  },
);

export const selectOutgoingDisplayStream = createSelector(
  (state: AppState) => state.realtime.outgoingDisplayStreamId,
  streamId => {
    if (streamId) {
      return getOutgoingDisplayStream();
    }
    return null;
  },
);

export const selectIsConnectedToPeer = (state: AppState) => {
  if (state.tour.isTouring) {
    return state.tour.isConnectedToPeer;
  }
  return state.realtime.isConnectedToPeer;
};

export const selectCallStatus = (state: AppState) => {
  if (state.tour.isTouring) {
    return state.tour.callStatus;
  }
  return state.realtime.callStatus;
};

export const selectTakeoverStatus = createSelector(
  (s: AppState) => selectActiveVisitor(s),
  (s: AppState) => s.tour.isTouring,
  (s: AppState) => s.tour.takeoverStatus,
  (activeVisitor, isTouring, tourTakeoverStatus): InteractionStatus => {
    if (isTouring) {
      return tourTakeoverStatus;
    }
    if (activeVisitor?.isTakeover) {
      return activeVisitor.isTakeoverAccepted ? 'active' : 'establishing';
    }
    return 'off';
  },
);

export const selectIsCallWaitingOnVisitor = createSelector(
  (s: AppState) => selectActiveVisitor(s),
  (s: AppState) => s.tour.isTouring,
  (s: AppState) => s.tour.takeoverStatus,
  (activeVisitor, isTouring) => {
    if (isTouring) {
      return false;
    }
    return activeVisitor?.isCallWaitingOnVisitor || false;
  },
);

// expose any kit / shared thunks we want access to outside realtime
export { dissociateVisitor };
export { associationWanted, kickAgentFromVisitor } from 'shared/realtime/association-kit';
export { requestDisplayshare, endDisplayshare } from 'shared/realtime/displayshare-kit';
export { startDisplaydemo, endDisplaydemo } from 'shared/realtime/displaydemo-kit';

export const {
  resetRealtimeState,
  serverStatusChanged,
  receivedServerBuildInfo,
  receivedWidgetState,
  serverTimeReceived,
  offlineDissociatedVisitor,
  peerStatusUpdated,
  takeoverWanted,
  takeoverRejected,
  callRejected,
  outgoingCameraStreamChanged,
  allowedVisitorVideo,
  incomingCameraStreamChanged,
  incomingDisplayStreamChanged,
  pauseVisitorAssociation,
  outgoingDisplayStreamChanged,
  setVisitorsCount,
  peerConnectionErrorUpdate,
  recordingWanted,
  callEndedDetailsChanged,
} = realtimeSlice.actions;
export default realtimeSlice.reducer;
